/**
 * 민감한 값을 모델에 보내지 않는다.
 *
 * **실측(2026-08-31):** 주민번호가 든 문장을 보내면 모델이 그 문장을 통째로 못 본 것처럼
 * 군다. `인사등록에 새 사원 넣어줘. 이름은 에이전트시험, 주민번호는 900101-1234568,
 * 입사일은 2026-08-31` 을 보내면 「주민등록번호와 **입사일** 값이 대화에 보이지
 * 않습니다」로 답하고 계획을 아예 안 만든다. 주민번호를 뺀 같은 문장은 초안까지 잘 간다.
 * 하이픈을 띄어쓰기로 바꿔도 같다. 세 번 재고 확인했다.
 *
 * 그래서 값을 **자리표로 바꿔** 모델에 준다. 모델은 `주민번호는 [민감값01]` 을 보고 어느
 * 칸에 넣을지만 정하고, 진짜 값은 화면에 넣기 직전에 되돌린다. 민감값은 브라우저 밖으로
 * 나가지 않는다 — 모델이 거르는 문제를 피하는 동시에 그 편이 옳다.
 */

/**
 * 가릴 것.
 *
 * **넓게 잡지 않는다.** 날짜(여덟 자리)나 금액까지 가리면 계획이 아무것도 못 세운다.
 * 지금은 주민등록번호꼴 열세 자리만 본다.
 */
const PATTERNS: { kind: string; label: RegExp; pattern: RegExp }[] = [
  // 주민등록번호. 앞에 붙은 라벨까지 함께 걷어낸다 — 값만 지우고 `주민번호는` 을
  // 남기면 모델이 그 말을 보고 다시 묻는다.
  {
    kind: 'rrn',
    label: /주민/,
    pattern: /(주민(등록)?번호[는은이가]?\s*[:=]?\s*)?\d{6}[-\s]?[1-8]\d{6}\b[,.、]?\s*/g,
  },
];

export type Masked = {
  /** 모델에 보낼 글. 민감값 자리에 자리표가 들어 있다. */
  text: string;
  /** 자리표 → 진짜 값. **이 표는 모델에 가지 않는다.** */
  values: Record<string, string>;
  /** 자리표 → 무엇인가(`rrn`). 어느 칸에 넣을지 우리가 정할 때 쓴다. */
  kinds: Record<string, string>;
};

/** 자리표 이름. 사람이 읽어도 무엇인지 알 수 있게 짓는다. */
function tokenFor(index: number): string {
  return `[민감값${String(index).padStart(2, '0')}]`;
}

/**
 * 민감값을 자리표로 바꾼다.
 *
 * 같은 값이 두 번 나오면 같은 자리표를 준다 — 다른 자리표를 주면 모델이 서로 다른
 * 값으로 읽는다.
 */
export function maskSensitive(text: string, seed: Record<string, string> = {}): Masked {
  const values: Record<string, string> = { ...seed };
  const kinds: Record<string, string> = {};
  let next = Object.keys(values).length + 1;
  let masked = text;
  for (const one of PATTERNS) {
    masked = masked.replace(one.pattern, (found) => {
      // **자리표조차 남기지 않는다.** 자리표를 남겼더니 모델이 판마다 다르게 굴었다 —
      // 어떤 판은 그대로 옮겼고 어떤 판은 「주민번호를 알려 주세요」로 되물었다.
      // 모델은 그 값이 있다는 사실조차 몰라야 한다. 어느 칸에 넣을지는 화면이 안다.
      const digits = /\d{6}[-\s]?[1-8]\d{6}/.exec(found)?.[0] ?? found;
      const token = tokenFor(next);
      next += 1;
      values[token] = digits;
      kinds[token] = one.kind;
      return '';
    });
  }
  return { text: masked.replace(/\s{2,}/g, ' ').trim(), values, kinds };
}

/**
 * 가려 둔 값을 **우리가 직접 그 칸에 넣는다.**
 *
 * 모델에게 자리표를 그대로 옮기라 일렀지만 지키지 않는 턴이 있다 — 실제로 자리표를 보고
 * 「주민번호를 다시 입력해 주세요」로 되물었다. 어느 칸인지는 화면이 알고 있으므로
 * 모델을 거치지 않는다. 계획이 이미 그 칸을 채웠으면 건드리지 않는다.
 */
export function fillMasked(
  fields: Record<string, string>,
  draftFields: { key: string; label?: string | null }[] | undefined,
  hidden: Masked,
): Record<string, string> {
  const tokens = Object.keys(hidden.kinds);
  if (!tokens.length || !draftFields?.length) return fields;
  const out = { ...fields };
  for (const token of tokens) {
    const rule = PATTERNS.find((one) => one.kind === hidden.kinds[token]);
    if (!rule) continue;
    const field = draftFields.find((one) => rule.label.test(one.label ?? ''));
    if (!field) continue;
    if (String(out[field.key] ?? '').trim()) continue;
    out[field.key] = hidden.values[token]!;
  }
  return out;
}

/** 가린 것이 있나. 없으면 부르는 쪽이 옛 길을 그대로 간다. */
export function hasMasked(values: Record<string, string>): boolean {
  return Object.keys(values).length > 0;
}

/**
 * 자리표를 진짜 값으로 되돌린다. **화면에 넣기 직전에만** 부른다.
 *
 * 모델이 자리표를 그대로 옮겨 적으면 여기서 풀린다. 모델이 자리표를 흉내 내 지어낸
 * 이름(`[민감값99]`)은 표에 없으므로 그대로 남고, 화면 검증이 그것을 잡는다.
 */
export function unmask(value: string, values: Record<string, string>): string {
  let out = value;
  for (const [token, real] of Object.entries(values)) {
    out = out.split(token).join(real);
  }
  return out;
}

/** 칸 전부를 되돌린다. */
export function unmaskFields(
  fields: Record<string, string>,
  values: Record<string, string>,
): Record<string, string> {
  if (!hasMasked(values)) return fields;
  return Object.fromEntries(Object.entries(fields).map(([key, value]) => [key, unmask(value, values)]));
}

/** 표의 행 전부를 되돌린다. */
export function unmaskRows(
  rows: Record<string, string>[],
  values: Record<string, string>,
): Record<string, string>[] {
  if (!hasMasked(values)) return rows;
  return rows.map((row) => unmaskFields(row, values));
}
